import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";

const defaults = {
  shipstationApiKey: "",
  shipstationApiSecret: "",
  shipstationBaseUrl: "",
  shipstationHasApiKey: false,
  shipstationHasApiSecret: false,
  shipstationApiKeyMasked: "",
  shipstationApiSecretMasked: "",
};

export default function ShipStationSettings({ instanceUniqueId }) {
  const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
  const headers = { headers: { accessToken: localStorage.getItem("accessToken") } };

  const [form, setForm] = useState(defaults);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const load = async () => {
    if (!instanceUniqueId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${SERVER}/ai/instances/${instanceUniqueId}/provider-settings`, headers);
      const shipstation = res.data?.shipstation || {};
      setForm({
        ...defaults,
        shipstationApiKey: shipstation.apiKey || "",
        shipstationApiSecret: shipstation.apiSecret || "",
        shipstationBaseUrl: shipstation.baseUrl || "",
        shipstationHasApiKey: shipstation.hasApiKey === true,
        shipstationHasApiSecret: shipstation.hasApiSecret === true,
        shipstationApiKeyMasked: shipstation.apiKeyMasked || "",
        shipstationApiSecretMasked: shipstation.apiSecretMasked || "",
      });
    } catch (e) {
      alert(e?.response?.data?.message || "Failed to load ShipStation settings.");
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        shipstationApiKey: form.shipstationApiKey,
        shipstationApiSecret: form.shipstationApiSecret,
        shipstationBaseUrl: form.shipstationBaseUrl,
      };
      const res = await axios.put(
        `${SERVER}/ai/instances/${instanceUniqueId}/provider-settings`,
        payload,
        headers,
      );

      const shipstation = res.data?.shipstation || {};
      setForm({
        ...defaults,
        shipstationApiKey: shipstation.apiKey || "",
        shipstationApiSecret: shipstation.apiSecret || "",
        shipstationBaseUrl: shipstation.baseUrl || "",
        shipstationHasApiKey: shipstation.hasApiKey === true,
        shipstationHasApiSecret: shipstation.hasApiSecret === true,
        shipstationApiKeyMasked: shipstation.apiKeyMasked || "",
        shipstationApiSecretMasked: shipstation.apiSecretMasked || "",
      });
      alert("ShipStation settings saved.");
    } catch (e) {
      alert(e?.response?.data?.message || "Failed to save ShipStation settings.");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceUniqueId]);

  return (
    <Card elevation={3} sx={{ borderRadius: 2 }}>
      <CardHeader
        title={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6">ShipStation</Typography>
            {loading ? <CircularProgress size={16} /> : null}
          </Box>
        }
        action={
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="outlined" onClick={load} disabled={loading || saving}>Refresh</Button>
            <Button variant="contained" onClick={save} disabled={loading || saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </Box>
        }
        subheader="Configure ShipStation credentials for this instance."
      />
      <Divider />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="password"
              label="ShipStation API Key"
              value={form.shipstationApiKey}
              onChange={setField("shipstationApiKey")}
              placeholder={form.shipstationHasApiKey ? form.shipstationApiKeyMasked || "Configured" : "Enter API Key"}
              helperText={form.shipstationHasApiKey ? `Saved: ${form.shipstationApiKeyMasked}` : "Leave blank to keep empty."}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="password"
              label="ShipStation API Secret"
              value={form.shipstationApiSecret}
              onChange={setField("shipstationApiSecret")}
              placeholder={form.shipstationHasApiSecret ? form.shipstationApiSecretMasked || "Configured" : "Enter API Secret"}
              helperText={form.shipstationHasApiSecret ? `Saved: ${form.shipstationApiSecretMasked}` : "Leave blank to keep empty."}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="ShipStation Base URL (optional)"
              value={form.shipstationBaseUrl}
              onChange={setField("shipstationBaseUrl")}
              placeholder="https://ssapi.shipstation.com"
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
